<template>
  <div class="o-fair-insurance">
    <modal
      :id="waiveInsuranceModalId"
      noCloseButton
      noEscapeByMask
      noEscapeByKeyboard
    >
      <h2>{{ $t('fairInsurance.modal.waiveInsurance.title') }}</h2>
      <p>{{ $t('fairInsurance.modal.waiveInsurance.text') }}</p>

      <div class="modal__action">
        <bos-button
          class="action--abort"
          type="button"
          small
          inline
          ghost
          @click.prevent="abortWaiveInsurance"
          >{{
            $t('fairInsurance.modal.waiveInsurance.action.abort')
          }}</bos-button
        >
        <bos-button
          class="action--waive"
          type="button"
          small
          inline
          @click.prevent="acceptWaiveInsurance"
          >{{
            $t('fairInsurance.modal.waiveInsurance.action.waive')
          }}</bos-button
        >
      </div>
    </modal>

    <template v-if="!insuranceFormSubmitted">
      <div class="o-container o-container--xlarge">
        <div class="fair-insurance__preinfo">
          <h2>1. {{ $t('fairInsurance.preinfo.title') }}</h2>
          <p>{{ $t('fairInsurance.preinfo.helperMessage.text') }}</p>
          <p>
            <a
              :href="
                $t('fairInsurance.preinfo.helperMessage.conditionLink.href')
              "
              download
              target="_blank"
              class="color-secondary"
              >
              <svg class="svgicon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1500 1500">
                <path class="cls-1" d="M624.62,1138.47h251.78c0-139.45,91.67-272.44,237.58-272.44h95.55v-228.54h-95.55c-116.21,0-227.25,58.11-227.25,202.72h-32.28V50h-206.59v790.2h-32.28c0-144.61-111.04-202.72-228.54-202.72h-95.55v228.54h95.55c145.9,0,237.58,132.99,237.58,272.44Z"/>
                <rect class="cls-1" x="134.4" y="1247.37" width="1231.19" height="202.63"/>
              </svg>
              {{
                $t('fairInsurance.preinfo.helperMessage.conditionLink.text')
              }}</a
            >
          </p>
        </div>

        <div class="fair-insurance__insurance">
          <bos-loader v-if="isLoading" />
          <template v-else>
            <div class="insurance__action">
              <bos-button
                v-if="isInsuranceFormDisabled"
                class="action--edit"
                type="button"
                @click.prevent="setInsuranceFormEnabled"
                >{{
                  $t('fairInsurance.insurance.action.editInsuranceButton')
                }}</bos-button
              >
            </div>

            <form class="insurance__form" novalidate>
              <fieldset
                v-for="(fieldset, fieldsetKey) in insuranceForm"
                :key="fieldsetKey"
              >
                

                <h2>
                  {{ Object.keys(insuranceForm).indexOf(fieldsetKey) + 2 }}. {{
                    $t(
                      `fairInsurance.insurance.form.fieldset.${fieldsetKey}.title`
                    )
                  }}
                </h2>
                <switcher
                  v-if="hasAllToggler(fieldsetKey)"
                  class="form__alltoggler"
                  :state="insuranceFormAllToggler[fieldsetKey]"
                  variant="primary"
                  :textOn="
                    $t('fairInsurance.insurance.form.allToggler.state.on')
                  "
                  :textOff="
                    $t('fairInsurance.insurance.form.allToggler.state.off')
                  "
                  :disabled="isInsuranceFormDisabled"
                  @switcherChange="toggleAllToggler(fieldsetKey)"
                />
                <form-control-insurance
                  v-for="(control, controlKey) in fieldset"
                  :key="controlKey"
                  :control="control"
                  :validator="$v.insuranceForm[fieldsetKey][controlKey]"
                  @controlChange="updateControl"
                />
              </fieldset>

              <div class="form__action">
                <bos-button
                  ghost
                  :disabled="isInsuranceFormDisabled"
                  @click.prevent="insuranceFormAbort"
                  >{{
                    $t('fairInsurance.insurance.form.action.abort')
                  }}</bos-button
                >

                <bos-button
                  :disabled="isInsuranceFormDisabled"
                  @click.prevent="insuranceFormSubmit"
                  hovergrow
                  >{{
                    $t('fairInsurance.insurance.form.action.submit')
                  }}</bos-button
                >
              </div>
            </form>
          </template>
        </div>
      </div>
    </template>
    <template v-else>
      <div class="o-container o-container--xlarge">
        <h2>{{ $t('fairInsurance.insurance.form.submitted.title') }}</h2>
        <p>{{ $t('fairInsurance.insurance.form.submitted.text') }}</p>
      </div>
    </template>
  </div>
</template>

<script>
import { required, requiredIf, minValue } from 'vuelidate/lib/validators'
import HelperMessage from '@/components/HelperMessage'
import Modal from '@/components/Modal'
import BosLoader from '@/components/BosLoader'
import BosButton from '@/components/BosButton'
import Switcher from '@/components/Switcher'
import FormControlInsurance from '@/components/FormControlInsurance'

export default {
  name: 'FairInsurance',
  props: {
    /*
    boothId: {
      type: String,
      required: true
    }
    */
  },
  data() {
    return {
      booth_id: null,
      fair_id: null,
      isInitialized: false,
      insurance: {},
      waiveInsuranceModalId: 'WaiveInsuranceModal',
      insuranceFormEnabled: false,
      insuranceFormSubmitted: false,
      insuranceFormAllToggler: {},
      insuranceForm: {
        mandatoryInsurance: {
          liabilityInsurance: {
            id: 'LiabilityInsurance',
            type: 'boolean',
            label:
              'fairInsurance.insurance.form.fieldset.mandatoryInsurance.controls.liabilityInsurance.label',
            togglerOn:
              'fairInsurance.insurance.form.fieldset.mandatoryInsurance.controls.liabilityInsurance.toggler.on',
            togglerOff:
              'fairInsurance.insurance.form.fieldset.mandatoryInsurance.controls.liabilityInsurance.toggler.off',
            description:
              'fairInsurance.insurance.form.fieldset.mandatoryInsurance.controls.liabilityInsurance.description',
            costs:
              'fairInsurance.insurance.form.fieldset.mandatoryInsurance.controls.liabilityInsurance.costs',
            value: true,
            disabled: () => this.isInsuranceFormDisabled
          },
          propertyInsurance: {
            id: 'PropertyInsurance',
            type: 'boolean',
            label:
              'fairInsurance.insurance.form.fieldset.mandatoryInsurance.controls.propertyInsurance.label',
            togglerOn:
              'fairInsurance.insurance.form.fieldset.mandatoryInsurance.controls.propertyInsurance.toggler.on',
            togglerOff:
              'fairInsurance.insurance.form.fieldset.mandatoryInsurance.controls.propertyInsurance.toggler.off',
            description:
              'fairInsurance.insurance.form.fieldset.mandatoryInsurance.controls.propertyInsurance.description',
            costs:
              'fairInsurance.insurance.form.fieldset.mandatoryInsurance.controls.propertyInsurance.costs',
            value: true,
            disabled: () => this.isInsuranceFormDisabled
          },
          exhibitionInsurance: {
            id: 'ExhibitionInsurance',
            type: 'boolean',
            label:
              'fairInsurance.insurance.form.fieldset.mandatoryInsurance.controls.exhibitionInsurance.label',
            togglerOn:
              'fairInsurance.insurance.form.fieldset.mandatoryInsurance.controls.exhibitionInsurance.toggler.on',
            togglerOff:
              'fairInsurance.insurance.form.fieldset.mandatoryInsurance.controls.exhibitionInsurance.toggler.off',
            description:
              'fairInsurance.insurance.form.fieldset.mandatoryInsurance.controls.exhibitionInsurance.description',
            costs:
              'fairInsurance.insurance.form.fieldset.mandatoryInsurance.controls.exhibitionInsurance.costs',
            value: true,
            disabled: () => this.isInsuranceFormDisabled
          }
        },
        insuranceAmountIncreaseRequest: {
          increasedAmountActive: {
            id: 'IncreasedAmountActive',
            type: 'boolean',
            label:
              'fairInsurance.insurance.form.fieldset.insuranceAmountIncreaseRequest.controls.increasedAmountActive.label',
            togglerOn:
              'fairInsurance.insurance.form.fieldset.insuranceAmountIncreaseRequest.controls.increasedAmountActive.toggler.on',
            togglerOff:
              'fairInsurance.insurance.form.fieldset.insuranceAmountIncreaseRequest.controls.increasedAmountActive.toggler.off',
            description:
              'fairInsurance.insurance.form.fieldset.insuranceAmountIncreaseRequest.controls.increasedAmountActive.description',
            value: false,
            disabled: () =>
              this.isInsuranceFormDisabled || this.disableAmountIncreaseRequest,
            dependants: {
              increasedAmount: {
                id: 'IncreasedAmount',
                type: 'text',
                placeholder:
                  'fairInsurance.insurance.form.fieldset.insuranceAmountIncreaseRequest.controls.increasedAmount.placeholder',
                hint:
                  'fairInsurance.insurance.form.fieldset.insuranceAmountIncreaseRequest.controls.increasedAmount.hint',
                value: '',
                disabled: () => this.isInsuranceFormDisabled
              }
            }
          }
        },
        optionalInsurance: {
          cargoInsurance: {
            id: 'CargoInsurance',
            type: 'boolean',
            label:
              'fairInsurance.insurance.form.fieldset.optionalInsurance.controls.cargoInsurance.label',
            togglerOn:
              'fairInsurance.insurance.form.fieldset.optionalInsurance.controls.cargoInsurance.toggler.on',
            togglerOff:
              'fairInsurance.insurance.form.fieldset.optionalInsurance.controls.cargoInsurance.toggler.off',
            description:
              'fairInsurance.insurance.form.fieldset.optionalInsurance.controls.cargoInsurance.description',
            costs:
              'fairInsurance.insurance.form.fieldset.optionalInsurance.controls.cargoInsurance.costs',
            value: false,
            disabled: () => this.isInsuranceFormDisabled,
            dependants: {
              breakableCargoValue: {
                id: 'BreakableCargoValue',
                type: 'text',
                placeholder:
                  'fairInsurance.insurance.form.fieldset.optionalInsurance.controls.breakableCargoValue.placeholder',
                hint:
                  'fairInsurance.insurance.form.fieldset.optionalInsurance.controls.breakableCargoValue.hint',
                costs:
                  'fairInsurance.insurance.form.fieldset.optionalInsurance.controls.breakableCargoValue.costs',
                value: '',
                disabled: () => this.isInsuranceFormDisabled
              }
            }
          }
        }
      }
    }
  },
  validations() {
    return {
      insuranceForm: {
        mandatoryInsurance: {
          liabilityInsurance: {
            value: {
              required
            }
          },
          propertyInsurance: {
            value: {
              required
            }
          },
          exhibitionInsurance: {
            value: {
              required
            }
          }
        },
        insuranceAmountIncreaseRequest: {
          increasedAmountActive: {
            value: {
              required
            },
            dependants: {
              increasedAmount: {
                value: {
                  required: requiredIf(
                    () =>
                      this.insuranceForm.insuranceAmountIncreaseRequest
                        .increasedAmountActive.value === true
                  ),
                  minValue: minValue(20000)
                }
              }
            }
          }
        },
        optionalInsurance: {
          cargoInsurance: {
            value: {},
            dependants: {
              breakableCargoValue: {
                value: {}
              }
            }
          }
        }
      }
    }
  },
  computed: {
    isLoading() {
      return this.$store.getters['insurance/isLoading']
    },
    hasInsurance() {
      return this.insurance.booth_id !== undefined
    },
    isInsuranceFormDisabled() {
      return this.hasInsurance && !this.insuranceFormEnabled
    },
    waiveInsurance() {
      return !this.insuranceFormAllToggler.mandatoryInsurance
    },
    disableAmountIncreaseRequest() {
      return (
        this.insuranceForm.mandatoryInsurance.propertyInsurance.value ===
          false &&
        this.insuranceForm.mandatoryInsurance.exhibitionInsurance.value ===
          false
      )
    }
  },
  methods: {
    getInsurance() {
      this.$store.dispatch('insurance/get', this.booth_id).then(response => {
        this.insurance = response || {}
        this.$nextTick(() => {
          this.mapInsuranceToForm()
          this.isInitialized = true
        })
      })
    },
    setInsuranceFormEnabled() {
      this.insuranceFormEnabled = true
    },
    hasAllToggler(fieldsetKey) {
      return Object.keys(this.insuranceForm[fieldsetKey]).length > 1
    },
    toggleAllToggler(fieldsetKey, value, updateStateOnly) {
      let fieldset = this.insuranceForm[fieldsetKey]

      this.$set(
        this.insuranceFormAllToggler,
        fieldsetKey,
        value !== undefined ? value : !this.insuranceFormAllToggler[fieldsetKey]
      )

      if (!updateStateOnly) {
        Object.keys(fieldset)
          .filter(
            controlKey =>
              this.insuranceForm[fieldsetKey][controlKey].type === 'boolean'
          )
          .forEach(controlKey => {
            this.insuranceForm[fieldsetKey][
              controlKey
            ].value = this.insuranceFormAllToggler[fieldsetKey]
          })
      }

      this.$forceUpdate()
    },
    updateAllTogglerState() {
      Object.keys(this.insuranceForm).forEach(fieldsetKey => {
        if (this.hasAllToggler(fieldsetKey)) {
          let states = Object.keys(this.insuranceForm[fieldsetKey])
            .filter(
              controlKey =>
                this.insuranceForm[fieldsetKey][controlKey].type === 'boolean'
            )
            .map(
              controlKey => this.insuranceForm[fieldsetKey][controlKey].value
            )

          this.toggleAllToggler(fieldsetKey, !states.includes(false), true)
        }
      })
    },
    updateControl(control) {
      if (control.type === 'boolean') {
        this.updateAllTogglerState()
      }
    },
    abortWaiveInsurance() {
      this.toggleAllToggler('mandatoryInsurance', true)
    },
    acceptWaiveInsurance() {
      this.$root.$emit(`modal:${this.waiveInsuranceModalId}:hide`)
    },
    insuranceFormAbort() {
      this.$router.push({
        name: 'FairDetail'
      })
    },
    mapInsuranceToForm() {
      if (this.hasInsurance) {
        this.insuranceForm.mandatoryInsurance.liabilityInsurance.value = this.insurance.liabilityInsurance
        this.insuranceForm.mandatoryInsurance.propertyInsurance.value = this.insurance.propertyInsurance
        this.insuranceForm.mandatoryInsurance.exhibitionInsurance.value = this.insurance.exhibitionInsurance

        this.insuranceForm.insuranceAmountIncreaseRequest.increasedAmountActive.value = this.insurance.increasedAmountActive
        this.insuranceForm.insuranceAmountIncreaseRequest.increasedAmountActive.dependants.increasedAmount.value = this.insurance.increasedAmount

        this.insuranceForm.optionalInsurance.cargoInsurance.value = this.insurance.cargoInsurance
        this.insuranceForm.optionalInsurance.cargoInsurance.dependants.breakableCargoValue.value = this.insurance.breakableCargoValue
      }

      this.updateAllTogglerState()
    },
    mapFormToInsurance() {
      const fair = this.$store.state.fair.rubinFairs.find(item =>
        item.booths.find(booth => booth.regiNr == this.$route.params.booth)
      )
      const booth = fair.booths.find(
        booth => booth.regiNr == this.$route.params.booth
      )
      const boothAddress = this.$store.getters['fair/getBoothAddress'](
        this.$route.params.booth
      )

      let exponant = undefined
      if (boothAddress !== undefined && boothAddress.data !== undefined) {
        if (
          boothAddress.data.adresseTyp == 'APADR' ||
          boothAddress.data.adresseTyp == 'EADR'
        ) {
          exponant =
            boothAddress.data.vorname + ' ' + boothAddress.data.nachname
        } else {
          exponant = boothAddress.data.firma1
        }
      }

      let additionalBooths = []
      fair.booths.forEach(booth => {
        if (booth.regiNr !== this.$route.params.booth)
          additionalBooths.push({
            regiNr: booth.regiNr,
            hall: booth.halleBezeichnungDe,
            boothNumber: booth.standnummer
          })
      })

      return {
        fairIdent: fair.idCode,
        teamEmail: fair.emailTeam,
        regiNr: this.$route.params.booth,
        hall: booth.halleBezeichnungDe,
        boothNumber: booth.standnummer,
        exponant,
        additionalBooths,
        booth_id: this.booth_id,
        liabilityInsurance: this.insuranceForm.mandatoryInsurance
          .liabilityInsurance.value,
        propertyInsurance: this.insuranceForm.mandatoryInsurance
          .propertyInsurance.value,
        exhibitionInsurance: this.insuranceForm.mandatoryInsurance
          .exhibitionInsurance.value,
        increasedAmountActive: this.insuranceForm.insuranceAmountIncreaseRequest
          .increasedAmountActive.value,
        increasedAmount: this.insuranceForm.insuranceAmountIncreaseRequest
          .increasedAmountActive.dependants.increasedAmount.value,
        cargoInsurance: this.insuranceForm.optionalInsurance.cargoInsurance
          .value,
        breakableCargoValue: this.insuranceForm.optionalInsurance.cargoInsurance
          .dependants.breakableCargoValue.value
      }
    },
    insuranceFormSubmit() {
      this.$v.$touch()

      if (!this.$v.$invalid) {
        this.$store.dispatch(
          'fair/resetBoothInsurance',
          this.$route.params.booth
        )
        let dispatchAction = this.hasInsurance ? 'update' : 'create'

        this.$store
          .dispatch(`insurance/${dispatchAction}`, this.mapFormToInsurance())
          .then(response => {
            this.$store.dispatch(
              'fair/getBoothInsurance',
              this.$route.params.booth
            )
            this.insuranceFormSubmitted = true
          })
      }
    }
  },
  mounted() {
    this.$store.dispatch('fair/getWithBooths')
  },
  created() {
    //this.booth_id = this.boothId
    this.booth_id = this.$route.params.booth
    this.fair_id = this.$route.params.code
    this.getInsurance()
  },
  watch: {
    '$route.params.booth'() {
      this.isInitialized = false
      this.booth_id = this.$route.params.booth
      this.getInsurance()
    },
    /*
    '$props.boothId'(boothId) {
      this.isInitialized = false
      this.booth_id = boothId
      this.getInsurance()
    },
    */
    waiveInsurance(state) {
      if (this.isInitialized) {
        this.$root.$emit(
          `modal:${this.waiveInsuranceModalId}:${state ? 'show' : 'hide'}`
        )
      }
    },
    disableAmountIncreaseRequest(disabled) {
      if (disabled)
        this.insuranceForm.insuranceAmountIncreaseRequest.increasedAmountActive.value = false
    }
  },
  components: {
    HelperMessage,
    Modal,
    BosLoader,
    BosButton,
    Switcher,
    FormControlInsurance
  }
}
</script>

<stlye lang="scss">
.o-fair-insurance {
  .modal-fullwrapper .modal-container {
    border-radius: 20px;
    box-shadow: 0 10px 16px 0 rgba(0,0,0,0.2),0 6px 20px 0 rgba(0,0,0,0.19) !important
  }
  .svgicon {
      width: 1rem;
      height: 1rem;
      fill: currentColor;
  }
}
</stlye>
<style lang="scss" scoped>
@import '@/assets/css/variables.scss';
@import '@/assets/css/mixins/clearfix.scss';


$fairInsurance-preinfo-margin: $component-margin !default;
$fairInsurance-insurance-margin: $component-margin !default;
$fairInsurance-form-heading-margin: 3rem 0 2rem !default;
$fairInsurance-insurance-action-margin: 3rem 0 2rem !default;
$fairInsurance-alltoggler-padding: 1rem !default;
$fairInsurance-form-action-margin: 2rem 0 0 !default;

h4, h3, h2 {
  font-weight: bold;
  color: $brand-primary;
}

.color-secondary {
  color: $brand-secondary;

  &:hover {
    color: $brand-primary;
  }
}


.o-fair-insurance {
  .fair-insurance__preinfo {
    margin: $fairInsurance-preinfo-margin;
  }

  .fair-insurance__insurance {
    margin: $fairInsurance-insurance-margin;

    .insurance__action {
      margin: $fairInsurance-insurance-action-margin;
    }

    .insurance__form {
      .form__alltoggler {
        padding: $fairInsurance-alltoggler-padding;
        justify-content: flex-end;
      }

      fieldset {
        margin: 0 0 2rem 0;
        padding: 0;
        border: 0 none;

        > h3 {
          margin: $fairInsurance-form-heading-margin;
        }

        &:first-child {
          > h3 {
            margin-top: 0;
          }
        }
      }

      .form__action {
        display: flex;
        justify-content: space-between;
        margin: $fairInsurance-form-action-margin;
      }
    }
  }
}
.action--abort {
  margin-right: 40px;
}
</style>
